<template>
  <div class="preselect__container" :class="containerClasses" :style="containerStyle">
    <div class="preselect--wrapper">
      <div v-if="title || $slots.subtitle" class="preselect__title--wrapper">
        <div v-if="title" class="preselect__title heading-h3 heading-thick">{{ title }}</div>
        <span v-if="$slots.subtitle" class="preselect__subtitle text--italic">
          <slot name="subtitle" />
        </span>
      </div>
      <div class="preselect__attributes">
        <div
          v-for="(field, index) in fields"
          :key="field.name"
          class="preselect__row"
          :class="[
            `preselect__row--${fields.length}-rows`,
            field.rowClass ? `preselect__row--${field.rowClass}` : '',
          ]"
        >
          <label :for="fieldId(field.name)" class="preselect__label">{{ field.label }}</label>
          <div v-if="loading">
            <span class="preselect__spinner" aria-hidden="true" />
          </div>
          <span v-else class="plain-selector">
            <select
              :id="fieldId(field.name)"
              :name="field.name"
              class="form__input form__input--choice"
              :value="selectedOptions[field.name] ?? ''"
              :disabled="isFieldDisabled(field.name, index)"
              @change="onSelectChange(field.name, $event)"
            >
              <option v-if="field.placeholder" value="">{{ field.placeholder }}</option>
              <option
                v-for="option in fieldOptions(field)"
                :key="`${field.name}-${getOptionValue(option)}`"
                :value="String(getOptionValue(option))"
              >
                {{ getOptionLabel(option) }}
              </option>
            </select>
          </span>
        </div>
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type {
  PreselectFieldConfig,
  PreselectOption,
  StagedPreselectSettings,
} from './StagedPreselectBlock.types';
import type { UseStagedPreselectResult } from './useStagedPreselect.types';

const props = defineProps<{
  title?: string;
  fields: PreselectFieldConfig[];
  colorSchema?: string;
  showFullWidth?: boolean;
  bgColor?: string;
  textColor?: string;
  settings?: StagedPreselectSettings;
  staged: UseStagedPreselectResult;
}>();

const {
  selectedOptions,
  availableOptions,
  loading,
  handleChange,
  isFieldDisabled,
  getOptionValue,
  getOptionLabel,
} = props.staged;

const containerClasses = computed(() => ({
  [`preselect__container--${props.colorSchema ?? 'primary'}`]: true,
  'preselect__container--wide': props.showFullWidth === true,
}));

const containerStyle = computed(() => {
  const style: Record<string, string> = {};
  if (props.bgColor) {
    style.background = props.bgColor;
  }
  if (props.textColor) {
    style.color = props.textColor;
  }
  return style;
});

const fieldId = (name: string): string => `preselect-${name}`;

const fieldOptions = (field: PreselectFieldConfig): PreselectOption[] => {
  const dynamic = availableOptions[field.name];
  if (dynamic?.length) {
    return dynamic;
  }
  return field.options ?? [];
};

const onSelectChange = (name: string, event: Event): void => {
  const target = event.target as HTMLSelectElement;
  handleChange(name, target.value);
};
</script>
